import type { ClaimWorkCounts } from '../../../store';

/**
 * Keep queue badges on the newest canonical snapshot across per-view RTK
 * caches.
 *
 * Generic on the CALLER'S counts shape, and deliberately not on the incoming
 * one. Callers hand-pin the contract's additions over the generated
 * `ClaimWorkCounts` (Home's `HomeCounts`, this tab's `ClaimWorkQueueCounts`),
 * and those additions are narrower than the generated index signature, so
 * requiring one type to satisfy both sides made every such call unresolvable.
 * The runtime answer is the same object either way: whichever snapshot is
 * newer, unchanged. Packet F's generated client removes the mismatch and this
 * signature can collapse back.
 */
export const selectNewerClaimWorkCounts = <T extends ClaimWorkCounts>(
  current: T | undefined,
  incoming: ClaimWorkCounts | undefined,
): T | undefined => {
  if (!incoming) return current;
  /* The cast is the one place the widening above is admitted: the value is
     the wire's counts, and the caller's type says which optional additions it
     expects to find on it. */
  const next = incoming as T;
  if (!current) return next;
  const currentComputedAt = Date.parse(current.computed_at);
  const incomingComputedAt = Date.parse(incoming.computed_at);
  if (!Number.isFinite(incomingComputedAt)) return current;
  if (!Number.isFinite(currentComputedAt)) return next;
  return incomingComputedAt >= currentComputedAt ? next : current;
};
